import { Component, type ErrorInfo, type ReactNode } from 'react';

import type { Chave } from '@vestigo/core/i18n-da-tela';

import { useT } from '../lib/idioma.js';

/**
 * ── A CERCA DE ERRO: uma tela que falha não leva a área inteira ──────────────
 *
 * ## O que o dono viu
 *
 * *«Se eu clico no botão voltar do navegador, a tela fica preta, e nada
 * aparece, então preciso dar refresh.»* Medido em produção em 04/09/2026: ao
 * voltar de uma aula de PDF, `Uncaught TypeError: C.destroy is not a function`
 * na desmontagem do leitor — e o React 19, sem uma cerca, **desmonta a raiz
 * inteira**. `#root` ficava vazio: fundo preto, sem cabeçalho, sem lista, sem
 * saída.
 *
 * A causa daquele erro está consertada no leitor. Esta cerca é para o PRÓXIMO:
 * um componente que quebre — na desmontagem, num efeito, num render — perde a
 * sua caixa, e o resto da sala continua de pé, com uma frase e um botão para
 * recarregar.
 *
 * `key` de quem embute: mudar a rota zera a cerca, para o erro de uma aula não
 * ficar preso na seguinte.
 *
 * ## POR QUE ESTAS DUAS FRASES NÃO SE TRADUZEM (23/10/2026)
 *
 * Quando a área inteira passou a falar a língua do aluno (`A8`), a conversão
 * em massa tentou pôr o `useT()` aqui e o React recusou: **isto é uma classe,
 * e classe não tem hooks**. Mas a razão de fundo é melhor do que a mecânica,
 * e é esta:
 *
 * **esta é a tela que aparece quando a área quebrou.** O dicionário da língua
 * do aluno chega por `import()` dentro do `ProvedorDeIdioma`, e esta cerca
 * envolve-o — ela tem de desenhar-se mesmo quando o provedor é exatamente o
 * que caiu. Uma frase de socorro que depende do que falhou não é frase de
 * socorro.
 *
 * O preço é nomeado e é pequeno: um aluno espanhol, no instante em que uma
 * caixa cai, lê duas frases em português e um botão que recarrega a página.
 * A alternativa — a tela de erro em branco porque o dicionário não carregou —
 * é a que o dono já viu, em 04/09, e é a razão desta cerca existir.
 */
export class CercaDeErro extends Component<
  { children: ReactNode; oQue?: Chave },
  { erro: Error | null }
> {
  override state: { erro: Error | null } = { erro: null };

  static getDerivedStateFromError(erro: Error) {
    return { erro };
  }

  override componentDidCatch(erro: Error, info: ErrorInfo) {
    /* Para o console e para quem depurar: a pilha de componentes diz QUAL
       caixa caiu, que é o que o erro sozinho não diz. */
    console.error('[vestigo/aluno] uma caixa caiu:', erro, info.componentStack);
  }

  override render() {
    if (!this.state.erro) return this.props.children;
    return (
      <div
        role="alert"
        data-vst-cerca-de-erro=""
        className="px-6 py-10 text-center text-sm"
        /*
         * ── AS RESERVAS SÃO O QUE FAZ A CERCA SER LEGÍVEL (23/09/2026) ───────
         *
         * As variáveis do tema são postas no elemento RAIZ DE CADA TELA
         * (`index.css` di-lo, e proíbe-as em `:root`). Uma cerca que embrulhe
         * algo de FORA das rotas — a faixa, o portão — não tem nenhuma delas
         * por cima: `var(--texto-suave)` resolveria para vazio, e a frase que
         * explica a queda ficaria invisível sobre o fundo escuro do documento.
         *
         * Uma mensagem de erro ilegível é a mesma tela preta que ela existe
         * para substituir. As reservas são as do tema de fábrica de
         * `lib/tema.ts`, e o `background` é explícito pelo mesmo motivo.
         */
        style={{
          background: 'var(--superficie, #16161d)',
          color: 'var(--texto-suave, #a1a1aa)',
          border: '1px dashed color-mix(in srgb, var(--texto-suave, #a1a1aa) 35%, transparent)',
          borderRadius: 'var(--raio, 8px)',
        }}
      >
        <CorpoDaQueda oQue={this.props.oQue} />
        <button
          type="button"
          onClick={() => window.location.reload()}
          className="mt-4 min-h-[44px] px-5 py-2.5 font-medium"
          style={{
            background: 'var(--destaque, #22c55e)',
            color: 'var(--sobre-destaque, #08130c)',
            borderRadius: 'var(--raio, 8px)',
          }}
        >
          <Recarregar />
        </button>
      </div>
    );
  }
}

/**
 * A frase da queda, num componente FUNÇÃO — porque a cerca é uma classe.
 *
 * `useT()` não entra numa classe React, e não precisa de entrar: o corpo da
 * queda é um componente próprio. E o `useT` desta casa é seguro FORA do
 * provedor — o contexto nasce com `dicionario: null` e `tradutorDe(null)`
 * serve português —, logo esta frase nunca depende de o dicionário ter
 * descido. É a propriedade que este ficheiro existe para garantir: *«a tela de
 * erro em branco porque o dicionário não carregou … é a razão desta cerca
 * existir»*.
 */
function CorpoDaQueda({ oQue }: { oQue?: Chave }) {
  const t = useT();
  return <p>{t('cerca.naoAbriuDireito', { oQue: t(oQue ?? 'cerca.estaParteDaTela') })}</p>;
}

/** O rótulo do único gesto desta tela. */
function Recarregar() {
  const t = useT();
  return <>{t('cerca.recarregarAPagina')}</>;
}
